<template>
  <div class="chat-search-page" :class="{ 'chat-search-page--rtl': isRTL }">
    <!-- Modern Search Header -->
    <div class="search-header">
      <div class="search-header__top">
        <button class="search-header__back" @click="goBack">
          <i class="bi bi-arrow-left"></i>
        </button>
        <h1 class="search-header__title">{{ $t('chat.search') || 'Search' }}</h1>
        <div class="search-header__spacer"></div>
      </div>
      
      <!-- Search Input -->
      <div class="search-input-container">
        <div class="search-input-wrapper">
          <i class="bi bi-search search-input__icon"></i>
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="text"
            class="search-input"
            :placeholder="searchPlaceholder"
            @input="onSearchInput"
            @keydown.escape="clearSearch"
          />
          <transition name="fade">
            <button v-if="searchQuery" class="search-input__clear" @click="clearSearch">
              <i class="bi bi-x-circle-fill"></i>
            </button>
          </transition>
        </div>
      </div>
      
      <!-- Segmented Control Tabs -->
      <div class="search-tabs">
        <button 
          class="search-tab"
          :class="{ 'search-tab--active': searchMode === 'conversations' }"
          @click="setSearchMode('conversations')"
        >
          <i class="bi bi-chat-dots"></i>
          <span>{{ $t('chat.search_conversations_tab') || 'Conversations' }}</span>
        </button>
        <button 
          class="search-tab"
          :class="{ 'search-tab--active': searchMode === 'messages' }"
          @click="setSearchMode('messages')"
        >
          <i class="bi bi-chat-text"></i>
          <span>{{ $t('chat.search_messages_tab') || 'Messages' }}</span>
        </button>
        <div class="search-tab__indicator" :class="{ 'search-tab__indicator--right': searchMode === 'messages' }"></div>
      </div>
    </div>
    
    <!-- Search Body -->
    <div class="search-body">
      <!-- Initial Empty State -->
      <div v-if="!searchQuery.trim() && !hasSearched" class="search-state search-state--empty">
        <div class="search-state__icon-wrapper">
          <i class="bi bi-search"></i>
        </div>
        <h3 class="search-state__title">{{ $t('chat.search_title') || 'Search Chats' }}</h3>
        <p class="search-state__text">
          {{ searchMode === 'conversations' 
            ? ($t('chat.search_conversations_hint') || 'Search by conversation name, participant, or group')
            : ($t('chat.search_messages_hint') || 'Search for messages across all your conversations')
          }}
        </p>
      </div>
      
      <!-- Loading State -->
      <div v-else-if="isSearching" class="search-state search-state--loading">
        <div class="search-spinner">
          <div class="search-spinner__ring"></div>
          <i class="bi bi-search search-spinner__icon"></i>
        </div>
        <p class="search-state__text">{{ $t('common.searching') || 'Searching...' }}</p>
      </div>
      
      <!-- Error State -->
      <div v-else-if="searchError" class="search-state search-state--error">
        <div class="search-state__icon-wrapper search-state__icon-wrapper--error">
          <i class="bi bi-exclamation-triangle"></i>
        </div>
        <h3 class="search-state__title">{{ $t('common.error') || 'Error' }}</h3>
        <p class="search-state__text">{{ searchError }}</p>
        <button class="search-state__btn" @click="retrySearch">
          <i class="bi bi-arrow-clockwise"></i>
          {{ $t('common.retry') || 'Retry' }}
        </button>
      </div>
      
      <!-- No Results -->
      <div v-else-if="hasSearched && results.length === 0" class="search-state search-state--no-results">
        <div class="search-state__icon-wrapper search-state__icon-wrapper--muted">
          <i class="bi bi-inbox"></i>
        </div>
        <h3 class="search-state__title">{{ $t('chat.search_no_results') || 'No Results' }}</h3>
        <p class="search-state__text">
          {{ $t('chat.no_results_for', { query: searchQuery }) || `No results found for "${searchQuery}"` }}
        </p>
        <p class="search-state__hint">{{ $t('chat.try_different_keywords') || 'Try different keywords or check your spelling' }}</p>
      </div>
      
      <!-- Results -->
      <div v-else-if="results.length > 0" class="search-results">
        <!-- Results Header -->
        <div class="search-results__header">
          <span class="search-results__count">
            {{ results.length }} {{ results.length === 1 
              ? ($t('chat.result') || 'result') 
              : ($t('chat.results') || 'results') 
            }}
          </span>
        </div>
        
        <!-- Conversation Results -->
        <template v-if="searchMode === 'conversations'">
          <div 
            v-for="conv in results" 
            :key="conv.id"
            class="result-card result-card--conversation"
            @click="openConversation(conv)"
          >
            <div class="result-card__avatar">
              <img 
                v-if="getConversationAvatar(conv)" 
                :src="getConversationAvatar(conv)" 
                @error="onAvatarError"
                alt=""
              />
              <div v-else class="result-card__avatar-placeholder">
                {{ getAvatarInitial(conv) }}
              </div>
              <div v-if="conv.unreadCount > 0" class="result-card__badge">
                {{ conv.unreadCount > 99 ? '99+' : conv.unreadCount }}
              </div>
            </div>
            <div class="result-card__content">
              <div class="result-card__row">
                <span class="result-card__title" v-html="highlightText(getDisplayName(conv), searchQuery)"></span>
                <span class="result-card__time">{{ formatTime(conv.lastMessageDate || conv.updatedAt) }}</span>
              </div>
              <div class="result-card__row">
                <span class="result-card__preview">{{ getLastMessagePreview(conv) }}</span>
                <i v-if="conv.custom?.type === 'group'" class="bi bi-people-fill result-card__type-icon"></i>
              </div>
            </div>
            <i class="bi bi-chevron-right result-card__arrow"></i>
          </div>
        </template>
        
        <!-- Message Results -->
        <template v-else>
          <div 
            v-for="msg in results" 
            :key="msg.id"
            class="result-card result-card--message"
            @click="openMessageInConversation(msg)"
          >
            <div class="result-card__avatar">
              <img 
                v-if="getMessageConversationAvatar(msg)" 
                :src="getMessageConversationAvatar(msg)" 
                @error="onAvatarError"
                alt=""
              />
              <div v-else class="result-card__avatar-placeholder">
                {{ getMessageAvatarInitial(msg) }}
              </div>
            </div>
            <div class="result-card__content">
              <div class="result-card__meta">
                <span class="result-card__sender">{{ getMessageSenderName(msg) }}</span>
                <span class="result-card__separator">•</span>
                <span class="result-card__conversation-name">{{ getMessageConversationName(msg) }}</span>
              </div>
              <div class="result-card__message" v-html="highlightText(msg.text || '', searchQuery)"></div>
              <div class="result-card__timestamp">
                <i class="bi bi-clock"></i>
                {{ formatFullTime(msg.createdAt) }}
              </div>
            </div>
            <i class="bi bi-chevron-right result-card__arrow"></i>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { ChatService } from '@/services/chat';
import { chatState } from '@/stores/chat';

export default {
  name: 'ChatSearch',
  
  data() {
    return {
      searchQuery: '',
      searchMode: 'conversations', // 'conversations' or 'messages'
      isSearching: false,
      searchError: null,
      hasSearched: false,
      results: [],
      conversations: [],
      conversationsMap: {},
      debounceTimer: null,
    };
  },
  
  computed: {
    isRTL() {
      return document.documentElement.dir === 'rtl' || 
             document.documentElement.lang === 'ar';
    },
    
    searchPlaceholder() {
      return this.searchMode === 'conversations'
        ? (this.$t('chat.search_conversations_placeholder') || 'Search by name or participant...')
        : (this.$t('chat.search_messages_placeholder') || 'Search in messages...');
    },
  },
  
  async mounted() {
    this.$nextTick(() => {
      this.$refs.searchInput?.focus();
    });
    
    await this.loadConversations();
  },
  
  beforeUnmount() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  },
  
  methods: {
    goBack() {
      this.$router.back();
    },
    
    async loadConversations() {
      try {
        const list = await ChatService.getConversations();
        this.conversations = list;
        this.conversationsMap = {};
        list.forEach(c => {
          this.conversationsMap[c.id] = c;
          if (c.talkjs_conversation_id) {
            this.conversationsMap[c.talkjs_conversation_id] = c;
          }
        });
      } catch (e) {
        console.error('[ChatSearch] Failed to load conversations:', e);
      }
    },
    
    setSearchMode(mode) {
      if (this.searchMode === mode) return;
      
      this.searchMode = mode;
      this.results = [];
      this.hasSearched = false;
      this.searchError = null;
      
      if (this.searchQuery.trim()) {
        this.performSearch();
      }
    },
    
    onSearchInput() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
      
      const query = this.searchQuery.trim();
      
      if (!query) {
        this.results = [];
        this.hasSearched = false;
        this.searchError = null;
        this.isSearching = false;
        return;
      }
      
      // Conversation search is instant (local filtering)
      if (this.searchMode === 'conversations') {
        this.searchConversations(query);
        return;
      }
      
      // Message search is debounced (API call)
      this.isSearching = true;
      this.debounceTimer = setTimeout(() => {
        this.searchMessages(query);
      }, 400);
    },
    
    searchConversations(query) {
      const lowerQuery = query.toLowerCase();
      
      this.results = this.conversations.filter(c => {
        const name = this.getDisplayName(c).toLowerCase();
        const subject = (c.subject || '').toLowerCase();
        
        // Also search in participant names
        let participantMatch = false;
        if (c.custom?.participants_details) {
          try {
            const participants = JSON.parse(c.custom.participants_details);
            participantMatch = participants.some(p => 
              (p.name || '').toLowerCase().includes(lowerQuery)
            );
          } catch (e) {}
        }
        
        return name.includes(lowerQuery) || subject.includes(lowerQuery) || participantMatch;
      });
      
      this.hasSearched = true;
      this.isSearching = false;
    },
    
    async searchMessages(query) {
      this.isSearching = true;
      this.searchError = null;
      
      try {
        const result = await ChatService.searchMessages({ keyword: query, limit: 100 });
        
        if (result.success) {
          this.results = result.results || [];
        } else {
          this.searchError = result.message || this.$t('chat.search_failed') || 'Search failed';
          this.results = [];
        }
      } catch (e) {
        console.error('[ChatSearch] Search error:', e);
        this.searchError = e.message || this.$t('chat.search_failed') || 'Search failed';
        this.results = [];
      } finally {
        this.isSearching = false;
        this.hasSearched = true;
      }
    },
    
    performSearch() {
      const query = this.searchQuery.trim();
      if (!query) return;
      
      if (this.searchMode === 'conversations') {
        this.searchConversations(query);
      } else {
        this.isSearching = true;
        this.searchMessages(query);
      }
    },
    
    retrySearch() {
      this.performSearch();
    },
    
    clearSearch() {
      this.searchQuery = '';
      this.results = [];
      this.hasSearched = false;
      this.searchError = null;
      this.$refs.searchInput?.focus();
    },
    
    openConversation(conv) {
      const conversationId = conv.talkjs_conversation_id || conv.id;
      this.$router.push({
        name: 'ChatConversation',
        params: { conversationId },
      });
    },
    
    openMessageInConversation(msg) {
      const conversationId = msg._conversationId || msg.conversationId;
      this.$router.push({
        name: 'ChatConversation',
        params: { conversationId },
        query: { messageId: msg.id, highlight: 'true' },
      });
    },
    
    getDisplayName(conv) {
      if (conv.custom?.display_name) return conv.custom.display_name;
      if (conv.subject) return conv.subject;
      
      if (conv.custom?.type === 'direct' && conv.custom?.participants_details) {
        try {
          const participants = JSON.parse(conv.custom.participants_details);
          const other = participants.find(p => String(p.id) !== String(chatState.userId));
          if (other) return other.name;
        } catch (e) {}
      }
      
      return conv.id;
    },
    
    getConversationAvatar(conv) {
      if (conv.photoUrl) return conv.photoUrl;
      if (conv.custom?.photo) return conv.custom.photo;
      
      if (conv.custom?.type === 'direct' && conv.custom?.participants_details) {
        try {
          const participants = JSON.parse(conv.custom.participants_details);
          const other = participants.find(p => String(p.id) !== String(chatState.userId));
          if (other?.avatar) return other.avatar;
        } catch (e) {}
      }
      
      return null;
    },
    
    getAvatarInitial(conv) {
      const name = this.getDisplayName(conv);
      return name ? name.charAt(0).toUpperCase() : '?';
    },
    
    getLastMessagePreview(conv) {
      if (conv.lastMessage?.text) return conv.lastMessage.text;
      if (conv.lastMessage?.attachment) return '📎 ' + (this.$t('chat.attachment') || 'Attachment');
      return '';
    },
    
    getMessageConversationAvatar(msg) {
      const conv = this.conversationsMap[msg._conversationId || msg.conversationId];
      return conv ? this.getConversationAvatar(conv) : null;
    },
    
    getMessageAvatarInitial(msg) {
      const conv = this.conversationsMap[msg._conversationId || msg.conversationId];
      if (conv) return this.getAvatarInitial(conv);
      return '?';
    },
    
    getMessageSenderName(msg) {
      if (String(msg.senderId) === String(chatState.userId)) {
        return this.$t('chat.you') || 'You';
      }
      return msg.custom?.username || msg.senderName || 'Unknown';
    },
    
    getMessageConversationName(msg) {
      const conv = this.conversationsMap[msg._conversationId || msg.conversationId];
      if (conv) return this.getDisplayName(conv);
      return '';
    },
    
    highlightText(text, query) {
      if (!text || !query) return this.escapeHtml(text || '');
      
      const escapedText = this.escapeHtml(String(text));
      const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escapedQuery})`, 'gi');
      return escapedText.replace(regex, '<mark>$1</mark>');
    },
    
    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    },
    
    formatTime(timestamp) {
      if (!timestamp) return '';
      
      const date = typeof timestamp === 'number' 
        ? new Date(timestamp) 
        : new Date(timestamp);
      
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const msgDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      
      const diffDays = Math.floor((today - msgDate) / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      } else if (diffDays === 1) {
        return this.$t('chat.yesterday') || 'Yesterday';
      } else if (diffDays < 7) {
        return date.toLocaleDateString([], { weekday: 'short' });
      } else {
        return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
      }
    },
    
    formatFullTime(timestamp) {
      if (!timestamp) return '';
      
      const date = typeof timestamp === 'number' 
        ? new Date(timestamp) 
        : new Date(timestamp);
      
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const msgDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      
      const diffDays = Math.floor((today - msgDate) / (1000 * 60 * 60 * 24));
      const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      
      if (diffDays === 0) {
        return (this.$t('chat.today') || 'Today') + ' • ' + timeStr;
      } else if (diffDays === 1) {
        return (this.$t('chat.yesterday') || 'Yesterday') + ' • ' + timeStr;
      } else {
        const dateStr = date.toLocaleDateString([], { month: 'short', day: 'numeric' });
        return dateStr + ' • ' + timeStr;
      }
    },
    
    onAvatarError(e) {
      e.target.style.display = 'none';
    },
  },
};
</script>

<style scoped>
/* ===== BASE STYLES ===== */
.chat-search-page {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  overflow: hidden;
}

.chat-search-page--rtl {
  direction: rtl;
}

/* ===== SEARCH HEADER ===== */
.search-header {
  background: linear-gradient(135deg, #00c444 0%, #00a344 100%);
  padding: 0 0 20px;
  position: relative;
  z-index: 10;
}

.search-header__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 12px;
}

.search-header__back {
  width: 40px;
  height: 40px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.25s ease;
  backdrop-filter: blur(4px);
}

.search-header__back i {
  font-size: 18px;
  color: #ffffff !important;
}

.search-header__back:hover {
  background: rgba(255, 255, 255, 0.35);
  transform: translateX(-2px);
}

.chat-search-page--rtl .search-header__back:hover {
  transform: translateX(2px);
}

.chat-search-page--rtl .search-header__back i {
  transform: scaleX(-1);
}

.search-header__title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.search-header__spacer {
  width: 40px;
}

/* ===== SEARCH INPUT ===== */
.search-input-container {
  padding: 0 20px;
  margin-bottom: 16px;
}

.search-input-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #ffffff;
  border-radius: 16px;
  padding: 14px 18px;
  box-shadow: 
    0 4px 20px rgba(0, 0, 0, 0.08),
    0 0 0 1px rgba(255, 255, 255, 0.5);
  transition: all 0.25s ease;
}

.search-input-wrapper:focus-within {
  box-shadow: 
    0 6px 24px rgba(0, 0, 0, 0.12),
    0 0 0 2px rgba(255, 255, 255, 0.8);
}

.search-input__icon {
  font-size: 18px;
  color: #9ca3af;
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 16px;
  font-weight: 500;
  color: #1f2937;
  outline: none;
}

.search-input::placeholder {
  color: #9ca3af;
  font-weight: 400;
}

.search-input__clear {
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.search-input__clear:hover {
  color: #ef4444;
  background: #fef2f2;
}

.search-input__clear i {
  font-size: 18px;
}

/* ===== SEGMENTED TABS ===== */
.search-tabs {
  display: flex;
  margin: 0 20px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 14px;
  padding: 5px;
  position: relative;
  gap: 4px;
}

.search-tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 20px;
  border: none;
  background: transparent;
  border-radius: 10px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  color: #ffffff;
  transition: all 0.25s ease;
  position: relative;
  z-index: 2;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.search-tab i {
  font-size: 18px;
}

.search-tab--active {
  color: #1f2937;
  background: #ffffff;
  text-shadow: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.search-tab:not(.search-tab--active):hover {
  background: rgba(255, 255, 255, 0.15);
}

.search-tab:not(.search-tab--active):active {
  background: rgba(255, 255, 255, 0.25);
}

.search-tab__indicator {
  display: none;
}

/* ===== SEARCH BODY ===== */
.search-body {
  flex: 1;
  overflow-y: auto;
  padding-top: 8px;
}

/* ===== SEARCH STATES ===== */
.search-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 40px 24px;
  text-align: center;
}

.search-state__icon-wrapper {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e0f2e9 0%, #d1fae5 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
}

.search-state__icon-wrapper i {
  font-size: 32px;
  color: #00c444;
}

.search-state__icon-wrapper--error {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
}

.search-state__icon-wrapper--error i {
  color: #ef4444;
}

.search-state__icon-wrapper--muted {
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
}

.search-state__icon-wrapper--muted i {
  color: #9ca3af;
}

.search-state__title {
  margin: 0 0 8px;
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
}

.search-state__text {
  margin: 0;
  font-size: 15px;
  color: #6b7280;
  line-height: 1.5;
  max-width: 280px;
}

.search-state__hint {
  margin: 12px 0 0;
  font-size: 13px;
  color: #9ca3af;
}

.search-state__btn {
  margin-top: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border: none;
  background: linear-gradient(135deg, #00c444 0%, #00a344 100%);
  color: #ffffff;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow: 0 4px 14px rgba(0, 196, 68, 0.3);
}

.search-state__btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 196, 68, 0.4);
}

/* ===== SEARCH SPINNER ===== */
.search-spinner {
  position: relative;
  width: 64px;
  height: 64px;
  margin-bottom: 24px;
}

.search-spinner__ring {
  position: absolute;
  inset: 0;
  border: 3px solid #e5e7eb;
  border-top-color: #00c444;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.search-spinner__icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 24px;
  color: #00c444;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* ===== SEARCH RESULTS ===== */
.search-results {
  padding: 0 16px 24px;
}

.search-results__header {
  padding: 12px 8px;
  margin-bottom: 8px;
}

.search-results__count {
  font-size: 13px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* ===== RESULT CARDS ===== */
.result-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: #ffffff;
  border-radius: 16px;
  margin-bottom: 10px;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.result-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

.result-card:active {
  transform: translateY(0);
}

.result-card__avatar {
  position: relative;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  overflow: visible;
  flex-shrink: 0;
}

.result-card__avatar img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.result-card__avatar-placeholder {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #00c444 0%, #00a344 100%);
  color: #ffffff;
  font-weight: 700;
  font-size: 20px;
}

.result-card__badge {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  background: #ef4444;
  color: #ffffff;
  border-radius: 11px;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #ffffff;
}

.result-card__content {
  flex: 1;
  min-width: 0;
}

.result-card__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.result-card__row:first-child {
  margin-bottom: 4px;
}

.result-card__title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.result-card__title :deep(mark) {
  background: linear-gradient(135deg, #fef08a 0%, #fde047 100%);
  color: inherit;
  padding: 1px 4px;
  border-radius: 4px;
  font-weight: 700;
}

.result-card__time {
  font-size: 12px;
  font-weight: 500;
  color: #9ca3af;
  white-space: nowrap;
  flex-shrink: 0;
}

.result-card__preview {
  font-size: 14px;
  color: #6b7280;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.result-card__type-icon {
  font-size: 14px;
  color: #9ca3af;
  flex-shrink: 0;
}

.result-card__arrow {
  font-size: 16px;
  color: #d1d5db;
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.result-card:hover .result-card__arrow {
  transform: translateX(4px);
  color: #00c444;
}

.chat-search-page--rtl .result-card:hover .result-card__arrow {
  transform: translateX(-4px);
}

/* ===== MESSAGE RESULT CARD ===== */
.result-card--message .result-card__content {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.result-card__meta {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.result-card__sender {
  font-size: 15px;
  font-weight: 600;
  color: #1f2937;
}

.result-card__separator {
  color: #d1d5db;
  font-size: 10px;
}

.result-card__conversation-name {
  font-size: 13px;
  color: #00c444;
  font-weight: 500;
}

.result-card__message {
  font-size: 14px;
  color: #6b7280;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.result-card__message :deep(mark) {
  background: linear-gradient(135deg, #fef08a 0%, #fde047 100%);
  color: #1f2937;
  padding: 1px 4px;
  border-radius: 4px;
  font-weight: 600;
}

.result-card__timestamp {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #9ca3af;
}

.result-card__timestamp i {
  font-size: 12px;
}

/* ===== TRANSITIONS ===== */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* ===== RESPONSIVE ===== */
@media (max-width: 768px) {
  .search-header__top {
    padding: 12px 16px 8px;
  }
  
  .search-input-container {
    padding: 0 16px;
    margin-bottom: 12px;
  }
  
  .search-tabs {
    margin: 0 16px;
  }
  
  .search-tab span {
    display: none;
  }
  
  .search-tab i {
    font-size: 20px;
  }
  
  .search-results {
    padding: 0 12px 20px;
  }
  
  .result-card {
    padding: 14px;
    border-radius: 14px;
  }
  
  .result-card__avatar {
    width: 48px;
    height: 48px;
  }
}

/* ===== DARK MODE ===== */
[data-bs-theme="dark"] .chat-search-page {
  background: linear-gradient(180deg, #0f172a 0%, #1e293b 100%);
}

[data-bs-theme="dark"] .search-input-wrapper {
  background: #1e293b;
  box-shadow: 
    0 4px 20px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.05);
}

[data-bs-theme="dark"] .search-input {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .search-input__icon {
  color: #64748b;
}

[data-bs-theme="dark"] .search-state__icon-wrapper {
  background: linear-gradient(135deg, #064e3b 0%, #065f46 100%);
}

[data-bs-theme="dark"] .search-state__icon-wrapper--error {
  background: linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%);
}

[data-bs-theme="dark"] .search-state__icon-wrapper--muted {
  background: linear-gradient(135deg, #334155 0%, #475569 100%);
}

[data-bs-theme="dark"] .search-state__title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .search-state__text {
  color: #94a3b8;
}

[data-bs-theme="dark"] .result-card {
  background: #1e293b;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

[data-bs-theme="dark"] .result-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}

[data-bs-theme="dark"] .result-card__title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .result-card__preview,
[data-bs-theme="dark"] .result-card__message {
  color: #94a3b8;
}

[data-bs-theme="dark"] .result-card__sender {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .result-card__title :deep(mark),
[data-bs-theme="dark"] .result-card__message :deep(mark) {
  background: linear-gradient(135deg, #854d0e 0%, #a16207 100%);
  color: #fef08a;
}

[data-bs-theme="dark"] .search-results__count {
  color: #94a3b8;
}
</style>
